import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { devLog } from '../../lib/dev-logger';
import { useToast } from '../ui/Toast';

const Login: React.FC = () => {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const navigate = useNavigate();
  const { signIn, signInWithMagicLink, isLoggedIn } = useAuth();

  useEffect(() => {
    if (isLoggedIn) {
      const rawUrl = new URLSearchParams(window.location.search).get('returnUrl') || '/';
      const redirectTo = rawUrl.startsWith('/') && !rawUrl.startsWith('//') ? rawUrl : '/';
      navigate(redirectTo, { replace: true });
    }
  }, [isLoggedIn, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) { toast.warning('Please enter your email and password.'); return; }
    setIsLoading(true);
    try {
      const { user, error } = await signIn(email, password);
      if (error) {
        devLog.error('Login error:', error);
        const msg = error.message || '';
        const status = (error as { status?: number }).status ?? 0;
        if (msg.includes('Invalid login credentials')) toast.error('Invalid email or password.');
        else if (msg.includes('Email not confirmed')) toast.warning('Email not verified. Please check your inbox.');
        else if (msg.includes('Too many requests') || status === 429) toast.error('Too many login attempts. Please try again in a few minutes.');
        // PR 7: friendly 5xx — generic supabase server errors ("Database error
        // querying schema" etc.) confused users into thinking it was a credential
        // problem. Surface that it's transient infra, not their fault.
        else if (msg.includes('Database error') || msg.includes('querying schema') || status >= 500) {
          toast.error('Login service is temporarily unavailable. Please try again in a minute.');
        }
        else toast.error('An error occurred during login: ' + msg);
        return;
      }
      if (user) {
        devLog.info('Login successful:', user.email);
        const rawReturnUrl = new URLSearchParams(window.location.search).get('returnUrl') || '/';
        const returnUrl = rawReturnUrl.startsWith('/') && !rawReturnUrl.startsWith('//') ? rawReturnUrl : '/';
        navigate(returnUrl, { replace: true });
      }
    } catch (error) {
      devLog.error('Login failed:', error);
      toast.error('An error occurred during login. Please try again.');
    } finally { setIsLoading(false); }
  };

  const handleMagicLink = async () => {
    const targetEmail = email.trim() || magicLinkEmail.trim();
    if (!targetEmail) { toast.warning('Please enter your email address first.'); return; }
    setIsLoading(true);
    try {
      const { error } = await signInWithMagicLink(targetEmail);
      if (error) {
        // PR 7: distinguish transient SMTP/rate-limit failures from real ones
        const msg = error.message || '';
        const status = (error as { status?: number }).status ?? 0;
        if (msg.includes('rate limit') || status === 429) {
          toast.error('Too many requests. Please wait a few minutes and try again.');
        } else if (status >= 500) {
          toast.error('Email service temporarily unavailable. Please try again shortly.');
        } else {
          toast.error('Failed to send login link: ' + msg);
        }
      } else {
        setMagicLinkSent(true);
        setMagicLinkEmail(targetEmail);
      }
    } catch { toast.error('An error occurred. Please try again.'); }
    finally { setIsLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel — Brand */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[540px] flex-shrink-0 bg-gradient-to-br from-forest-600 via-forest-800 to-forest-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full border border-white/10" />
          <div className="absolute top-1/4 right-0 w-64 h-64 rounded-full border border-white/10" />
          <div className="absolute bottom-20 left-16 w-48 h-48 rounded-full bg-white/5" />
          <div className="absolute -bottom-10 -right-10 w-72 h-72 rounded-full border border-white/5" />
        </div>
        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full">
          <Link to="/" className="inline-flex items-baseline">
            <span className="font-display text-2xl font-bold tracking-[-0.04em] text-white">Libera</span><span className="font-display text-2xl font-bold tracking-[-0.04em] text-gold-400">Cure</span>
          </Link>
          <div>
            <h2 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-4">
              Your Trusted<br />Global Pharmacy
            </h2>
            <p className="text-forest-100 max-w-sm leading-relaxed">
              Quality medications at affordable prices, delivered worldwide with care and discretion.
            </p>
          </div>
          <div className="space-y-4">
            {[
              { title: 'Secure & Private', desc: '256-bit SSL encrypted', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /> },
              { title: 'Global Delivery', desc: 'Ships to 50+ countries', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /> },
              { title: 'Verified Products', desc: 'FDA-approved generics', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /> },
            ].map((item) => (
              <div key={item.title} className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-4.5 h-4.5 text-forest-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">{item.icon}</svg>
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{item.title}</p>
                  <p className="text-forest-200/70 text-xs">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel — Form */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-12 py-12 bg-cream-100">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <div className="lg:hidden mb-10">
            <Link to="/" className="inline-flex items-baseline">
              <span className="font-display text-2xl font-bold tracking-[-0.04em] text-forest-800">Libera</span><span className="font-display text-2xl font-bold tracking-[-0.04em] text-gold-400">Cure</span>
            </Link>
          </div>

          <h1 className="text-3xl font-display font-bold text-forest-900 mb-1 tracking-tight">Welcome back</h1>
          <p className="text-warm-500 mb-8">Sign in to your account to continue</p>

          {/* Magic Link sent confirmation */}
          {magicLinkSent && (
            <div className="mb-6 p-4 bg-forest-50 border border-forest-200 rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-5 h-5 text-forest-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-semibold text-forest-800">Login link sent!</span>
              </div>
              <p className="text-xs text-forest-600">Check your inbox at <strong>{magicLinkEmail}</strong> and click the link to sign in.</p>
            </div>
          )}

          {/* Standard login form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-forest-700 mb-1.5">Email address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-cream-50 border border-warm-300 rounded-xl text-sm focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 outline-none transition-all placeholder-warm-400"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="text-sm font-medium text-forest-700">Password</label>
                <Link to="/find-password" className="text-xs text-gold-400 hover:text-forest-800 font-medium">
                  Forgot password?
                </Link>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-cream-50 border border-warm-300 rounded-xl text-sm focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 outline-none transition-all"
                required
              />
            </div>

            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 text-forest-700 border-warm-300 rounded focus:ring-forest-500/20"
              />
              <span className="text-sm text-warm-600">Keep me signed in</span>
            </label>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-forest-800 hover:bg-forest-700 disabled:bg-forest-300 text-cream-100 py-3 rounded-lg border border-forest-700/50 font-semibold text-sm transition-all cursor-pointer flex items-center justify-center shadow-sm"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-cream-100 border-t-transparent rounded-full animate-spin" />
              ) : 'Sign In'}
            </button>
          </form>

          {/* Magic Link — secondary */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-warm-200" /></div>
            <div className="relative flex justify-center text-xs"><span className="px-4 bg-cream-100 text-warm-400 uppercase tracking-wider">or</span></div>
          </div>
          <button
            type="button"
            onClick={handleMagicLink}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-cream-50 border border-warm-300 rounded-lg text-sm font-medium text-forest-700 hover:bg-warm-100 hover:border-warm-400 disabled:opacity-50 transition-all cursor-pointer"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Sign in with Email Link
          </button>
          <p className="text-xs text-warm-400 text-center mt-2">No password needed — uses the email above</p>

          <p className="mt-6 text-center text-sm text-warm-500">
            Don't have an account?{' '}
            <Link to="/signup" className="text-gold-400 hover:text-forest-800 font-semibold">
              Create account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
